import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import SecondarySidebar from "../../components/SideBar/SecondarySidebar";
import { FaUser, FaUsers, FaUserShield, FaBuilding, FaCalculator, FaMapMarkerAlt, FaToggleOn } from "react-icons/fa";
import { RiImportFill } from "react-icons/ri";
import HeaderCard from "../../components/Header/HeaderCard";
import User from "./Tabs/User";
import UsersList from "./Tabs/UsersList";
import AccesDroits from "./Tabs/AccesDroits";
import Company from "./Tabs/Company";
import Security from "./Tabs/Security";
import Import from "./Tabs/Import";
import Accounting from "./Tabs/Accounting";
import Offres from "./Tabs/Offres";
import Modal from "../../components/Utils/ModalShow";
import ActionDropdown from "../../components/Utils/ActionDropdown";
import ImportExport from "../../components/Tools/ImportExport";
import ImportExportButtons from "../../components/Tools/ImportExportButtons";
import {notify} from "../../components/Utils/Notifications";
import { apiService } from "../../components/Config/api.service";
import { signalerErreur } from "../../components/Utils/signalerErreur";
import ExportAccountingModal from "./Tabs/Account/ExportAccountingModal";
import ImportAccountingModal from "./Tabs/Account/ImportAccountingModal";
import { FaPlus } from "react-icons/fa";
import SalaryHistory from './Tabs/SalaryHistory';
import ConfigurationPointage from '../Pointage/ConfigurationPointage';
import { Navigate } from 'react-router-dom';
import { canAccessSettings, canManageAccountingSetup, canCreateCompany, canManageUsers, fetchCurrentUser } from '../../components/Auth/permissions';
import { ongletsDe } from '../../components/Navigation/navigation';
import useOnglet from '../../components/Navigation/useOnglet';

const Settings = () => {
  const { t } = useTranslation();
  const menuItems = [
    { id: "societe", label: t('settingsPage.company'), icon: <FaBuilding />, description: t('settingsPage.companyDescription') },
    { id: "users_list", label: t('settingsPage.usersAccess'), icon: <FaUsers />, description: t('settingsPage.usersAccessDescription'), adminOnly: true },
    { id: "acces_droits", label: t('settingsPage.accesDroits'), icon: <FaUserShield />, description: t('settingsPage.accesDroitsDescription') },
    { id: "utilisateur", label: t('settingsPage.myProfile'), icon: <FaUser />, description: t('settingsPage.myProfileDescription') },
    { id: "comptabilite", label: t('settingsPage.accounting'), icon: <FaCalculator />, description: t('settingsPage.accountingDescription') },
    { id: "configuration_pointage", label: t('settingsPage.pointage'), icon: <FaMapMarkerAlt />, description: t('settingsPage.pointageDescription') },
    { id: "offres", label: t('settingsPage.offres'), icon: <FaToggleOn />, description: t('settingsPage.offresDescription') },
    // { id: "import_export", label: "Import/Export", icon: <RiImportFill />, description: "Gestion des données" },
    // { id: 'salary-history', label: 'Historique Salaires', icon: <FaCalculator />, description: 'Visualisez l\'historique des salaires' },
  ];
  const [showAddSocietyModal, setShowAddSocietyModal] = useState(false);
  const [newSociety, setNewSociety] = useState({ name: "", address: "", is_independent: false });
  const [isLoading, setIsLoading] = useState(false);
  const [companyCreated, setCompanyCreated] = useState(null); // {id, name}
  const [exportLoading, setExportLoading] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [categorizedRules, setCategorizedRules] = useState({});
  const [categoriesMeta, setCategoriesMeta] = useState({});
  const [defaultAccounts, setDefaultAccounts] = useState({});
  const [currentUser, setCurrentUser] = useState(null);
  const [isUserLoading, setIsUserLoading] = useState(true);
  // New user-related state
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [newUser, setNewUser] = useState({ name: "", email: "", role: "user" });
  const [isCreateUserLoading, setIsCreateUserLoading] = useState(false);
  const [createdUserId, setCreatedUserId] = useState(null);

  const user = currentUser || {};
  const isSuperadmin = user.role === 'superadmin';
  // console.log(user);
  const multicompany = user.multicompany;
  const settingsAllowed = currentUser ? canAccessSettings(currentUser) : false;
  const companyCreationAllowed = currentUser ? canCreateCompany(currentUser) : false;
  const accountingSetupAllowed = currentUser ? canManageAccountingSetup(currentUser) : false;
  const userManagementAllowed = currentUser ? canManageUsers(currentUser) : false;

  // Chaque onglet porte sa propre capacité, dans la carte de navigation
  // (Yros/Kavero#416). La porte de l'écran est au seuil `rh` depuis
  // Yros/Kavero#358 : elle ne garde plus rien à elle seule. Ici, chaque
  // onglet visible reçoit son icône et sa description.
  const visibleMenuItems = ongletsDe('/settings', currentUser, t).map((onglet) => ({
    ...menuItems.find((item) => item.id === onglet.id),
    ...onglet,
  }));
  // L'onglet ouvert vit dans l'adresse, et seulement parmi les visibles.
  const [activeTab, setActiveTab] = useOnglet(visibleMenuItems);

  const getTabTitle = () => {
    const tab = visibleMenuItems.find(item => item.id === activeTab);
    return tab ? tab.label : t('settingsPage.title');
  };

  const getTabDescription = () => {
    const tab = visibleMenuItems.find(item => item.id === activeTab);
    return tab ? tab.description : "";
  };

  const handleImport = () => {
    console.log(categorizedRules);
    setShowImportModal(true);
  };
  const handleExport = async () => {
    setShowExportModal(true);
  };

  const handleAddSociety = async () => {
    setIsLoading(true);
    try {
      const payload = {
        name: newSociety.name,
        address: newSociety.address,
        is_independent: newSociety.is_independent,
      };
      const response = await apiService.saveElement("/company/create", payload);
      if (response.data.success) {
        notify(t('settingsPage.newCompanyCreated'), false, 2);
        setCompanyCreated({
          id: response.data.company_id,
          name: newSociety.name
        });
      } else {
        notify(t('settingsPage.companyCreateError'), false, 3);
        setCompanyCreated(null);
      }
    } catch (error) {
      const message = apiService.getApiErrorMessage(error, t('settingsPage.companyCreateError'));
      notify(message, false, 3);
      setCompanyCreated(null);
    }
    setIsLoading(false);
    setNewSociety({ name: "", address: "", is_independent: false });
  };

  const handleMigrate = async () => {
    if (!companyCreated?.id) return;
    setIsLoading(true);
    try {
      const switchResponse = await apiService.saveElement(`/switch_company/${companyCreated.id}`, {});
      if (switchResponse.data.active_company_name) {
        localStorage.setItem('companyName', switchResponse.data.active_company_name);
        window.location.reload();
      }
    } catch (error) {
      signalerErreur(error, 'Settings.handleMigrate');
      notify(t('settingsPage.migrationError'), false, 3);
      setIsLoading(false);
    }
  };

  const handleCloseModal = () => {
    setShowAddSocietyModal(false);
    setCompanyCreated(null);
    setIsLoading(false);
    setNewSociety({ name: "", address: "", is_independent: false });
  };

  // Déplace cette fonction ici pour qu'elle soit accessible dans le callback
  const fetchCategorizedRules = async () => {
    const res = await apiService.getElement("/accounting/codes");
    if (res.data) {
      setCategorizedRules(res.data.categorized_rules || {});
      setCategoriesMeta(res.data.categories_meta || {});
      setDefaultAccounts(res.data.default_accounts || {});
      
      // Notifier l'utilisateur si le plan comptable a été initialisé automatiquement
      if (res.data.chart_initialized) {
        notify(t('settingsPage.chartInitialized'), false, 2);
      }
    }
  };

  const handleAddUser = async () => {
    setIsCreateUserLoading(true);
    try {
      // Plus de mot de passe choisi par l'admin : l'utilisateur reçoit une
      // invitation, dont le lien ouvre la base du pays courant (Yros/payor_enterprise#319).
      const payload = {
        name: newUser.name,
        email: newUser.email,
        role: newUser.role,
        pays: localStorage.getItem('selectedCountryCode') || 'sn',
      };
      const res = await apiService.saveElement("/company/create_user", payload);
      if (res.data?.user_id) {
        notify(t('settingsPage.invitationSent', { email: newUser.email }), false, 2);
        setCreatedUserId(res.data.user_id);
      } else {
        notify(res.data?.error || t('settingsPage.userCreateError'), false, 3);
        setCreatedUserId(null);
      }
    } catch (e) {
      const message = apiService.getApiErrorMessage(e, t('settingsPage.userCreateError'));
      notify(message, false, 3);
      setCreatedUserId(null);
    }
    setIsCreateUserLoading(false);
  };

  const handleCloseUserModal = () => {
    setShowAddUserModal(false);
    setNewUser({ name: "", email: "", role: "user" });
    setIsCreateUserLoading(false);
    setCreatedUserId(null);
  };

  useEffect(() => {
    if (activeTab === "comptabilite") {
      fetchCategorizedRules();
    }
  }, [activeTab]);

  useEffect(() => {
    let isMounted = true;

    fetchCurrentUser()
      .then((nextUser) => {
        if (isMounted) {
          setCurrentUser(nextUser);
          setIsUserLoading(false);
        }
      })
      .catch(() => {
        if (isMounted) {
          setIsUserLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, []);

  if (!isUserLoading && !settingsAllowed) {
    return <Navigate to="/403" replace />;
  }

  if (isUserLoading) {
    return null;
  }

  return (
  <>
    <div className="space-y-6">
      <HeaderCard
        title={t('settingsPage.title')}
        text={t('settingsPage.subtitle')}
      />

      <div className="flex gap-6">
        {/* Sidebar */}
        <div className="w-80 flex-shrink-0 self-start sticky top-6">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center gap-3">                
                <h3 className="text-lg font-semibold text-gray-800">{t('settingsPage.configuration')}</h3>
              </div>
              <p className="text-sm text-gray-600 mt-1">{t('settingsPage.manageAppSettings')}</p>
            </div>
            <div className="p-2">
              <SecondarySidebar
                menuItems={visibleMenuItems}
                setActiveTab={setActiveTab}
                activeTab={activeTab}
              />
            </div>
          </div>
        </div>

        {/* Content Area */}
        <div className="flex-1 min-w-0">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center gap-3">
                <div className="bg-blue-100 rounded-full p-2">
                  {visibleMenuItems.find(item => item.id === activeTab)?.icon}
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-800">{getTabTitle()}</h3>
                  <p className="text-sm text-gray-600">{getTabDescription()}</p>
                </div>
                {activeTab === "societe" && multicompany && companyCreationAllowed && (
                  <ActionDropdown
                    actions={[
                      {
                        label: t('settingsPage.addCompany'),
                        icon: <FaBuilding />,
                        onClick: () => setShowAddSocietyModal(true),
                      },
                    ]}
                  />
                )}
                {activeTab === "utilisateur" && multicompany && userManagementAllowed && (
                  <ActionDropdown
                    actions={[
                      {
                        label: t('settingsPage.addUser'),
                        icon: <FaPlus />,
                        onClick: () => setShowAddUserModal(true),
                      },
                    ]}
                  />
                )}
                {activeTab === "comptabilite" && accountingSetupAllowed && (
                  <ActionDropdown
                    actions={[
                      {
                        label: t('settingsPage.importExport'),
                        onClick: () => {},
                        custom: (
                          <ImportExport
                            onImport={handleImport}
                            onExport={handleExport}
                            loading={exportLoading}
                          /> 
                        )
                      },
                    ]}
                  />
                )}
              </div>
            </div>
            <div className="p-6">
              {activeTab === "societe" && <Company isSuperadmin={isSuperadmin} />}
              {activeTab === "utilisateur" && <User />}
              {activeTab === "users_list" && <UsersList />}
              {activeTab === "acces_droits" && <AccesDroits />}
              {activeTab === "comptabilite" && (
                <Accounting
                  categorizedRules={categorizedRules}
                  setCategorizedRules={setCategorizedRules}
                  categoriesMeta={categoriesMeta}
                  defaultAccounts={defaultAccounts}
                  setDefaultAccounts={setDefaultAccounts}
                  onSaved={fetchCategorizedRules}
                  initialSearch={new URLSearchParams(window.location.search).get("rule") || ""}
                />
              )}
              {activeTab === "securite" && <Security />}
              {activeTab === "configuration_pointage" && <ConfigurationPointage />}
              {activeTab === "offres" && <Offres societeActiveId={user.company_active?.id} />}
              {/* à décommenter quand les fonctionnalités seront prêtes */}
              {/* {activeTab === "import_export" && <Import />}
              {activeTab === "salary-history" && <SalaryHistory />} */}
            </div>
          </div>
        </div>
      </div>
    </div>
    {/* Modal for adding society - Example implementation */}
      <Modal
        isOpen={showAddSocietyModal}
        onClose={handleCloseModal}
        title={t('settingsPage.addCompany')}
        headerIcon={<FaBuilding />}
        footer={
          companyCreated ? (
            <>
              <button
                className="px-4 py-2 rounded bg-gray-200 text-gray-700 mr-2"
                onClick={handleCloseModal}
                type="button"
                disabled={isLoading}
              >
                {t('customReportManager.close')}
              </button>
              <button
                className="px-4 py-2 rounded bg-[--color-primary] text-white"
                onClick={handleMigrate}
                type="button"
                disabled={isLoading}
              >
                {isLoading ? t('settingsPage.migrating') : t('settingsPage.migrateToCompany')}
              </button>
            </>
          ) : (
            <>
              <button
                className="px-4 py-2 rounded bg-gray-200 text-gray-700 mr-2"
                onClick={handleCloseModal}
                type="button"
                disabled={isLoading}
              >
                {t('basePrimeForm.cancel')}
              </button>
              <button
                className="px-4 py-2 rounded bg-[--color-primary] text-white"
                onClick={handleAddSociety}
                type="button"
                disabled={isLoading}
              >
                {isLoading ? t('settingsPage.creating') : t('settingsPage.create')}
              </button>
            </>
          )
        }
      >
        {isLoading && !companyCreated && (
          <div className="text-center py-6">
            <span className="animate-spin inline-block mr-2">&#9696;</span>
            {t('settingsPage.creatingCompanyInProgress')}
          </div>
        )}
        {!isLoading && !companyCreated && (
          <form
            onSubmit={e => {
              e.preventDefault();
              handleAddSociety();
            }}
          >
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">{t('settingsPage.companyName')}</label>
              <input
                type="text"
                className="w-full border rounded px-3 py-2"
                value={newSociety.name}
                onChange={e => setNewSociety({ ...newSociety, name: e.target.value })}
                required
                disabled={isLoading}
              />
            </div>
            <div className="mb-2">
              <label className="block text-sm font-medium mb-1">{t('settingsPage.address')}</label>
              <input
                type="text"
                className="w-full border rounded px-3 py-2"
                value={newSociety.address}
                onChange={e => setNewSociety({ ...newSociety, address: e.target.value })}
                disabled={isLoading}
              />
            </div>
            {isSuperadmin && (
              <div className="mb-4">
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    className="w-5 h-5 text-[--color-primary] rounded border-gray-300 focus:ring-[--color-primary]"
                    checked={newSociety.is_independent}
                    onChange={e => setNewSociety({ ...newSociety, is_independent: e.target.checked })}
                    disabled={isLoading}
                  />
                  <div className="ml-3">
                    <span className="text-gray-800 font-medium block">{t('settingsPage.independentCompany')}</span>
                    <span className="text-xs text-gray-500">{t('settingsPage.independentCompanyHint')}</span>
                  </div>
                </label>
              </div>
            )}
          </form>
        )}
        {companyCreated && (
          <div className="text-center py-6">
            <div className="text-[--color-primary] font-bold mb-2">
              {t('settingsPage.companyCreatedPrefix')} <span className="underline">{companyCreated.name}</span> {t('settingsPage.companyCreatedSuffix')}
            </div>
            <div className="mb-4">{t('settingsPage.switchToCompanyPrompt')}</div>
          </div>
        )}
      </Modal>
      <ExportAccountingModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        categorizedRules={categorizedRules}
        categoriesMeta={categoriesMeta}
      />
      <ImportAccountingModal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        supportsDryRun={Object.values(categorizedRules || {}).some((rules) => (rules || []).some((rule) => 'account_debit_origin' in rule))}
        onSuccess={() => {
          setActiveTab("comptabilite");
          fetchCategorizedRules();
          notify(t('settingsPage.dataImportedSuccess'), false, 2);
        }}
      />
      {/* Modal add user */}
      <Modal
        isOpen={showAddUserModal}
        onClose={handleCloseUserModal}
        title={t('settingsPage.addUser')}
        headerIcon={<FaUser />}
        footer={
          createdUserId ? (
            <button
              className="px-4 py-2 rounded bg-[--color-primary] text-white"
              onClick={handleCloseUserModal}
              type="button"
              disabled={isCreateUserLoading}
            >
              {t('customReportManager.close')}
            </button>
          ) : (
            <>
              <button
                className="px-4 py-2 rounded bg-gray-200 text-gray-700 mr-2"
                onClick={handleCloseUserModal}
                type="button"
                disabled={isCreateUserLoading}
              >
                {t('basePrimeForm.cancel')}
              </button>
              <button
                className="px-4 py-2 rounded bg-[--color-primary] text-white"
                onClick={handleAddUser}
                type="button"
                disabled={isCreateUserLoading}
              >
                {isCreateUserLoading ? t('settingsPage.creating') : t('settingsPage.create')}
              </button>
            </>
          )
        }
      >
        {isCreateUserLoading && !createdUserId && (
          <div className="text-center py-6">
            <span className="animate-spin inline-block mr-2">&#9696;</span>
            {t('settingsPage.creatingUserInProgress')}
          </div>
        )}
        {!isCreateUserLoading && !createdUserId && (
          <form
            onSubmit={e => {
              e.preventDefault();
              handleAddUser();
            }}
          >
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">{t('absenceType.name')}</label>
              <input
                type="text"
                className="w-full border rounded px-3 py-2"
                value={newUser.name}
                onChange={e => setNewUser({ ...newUser, name: e.target.value })}
                required
                disabled={isCreateUserLoading}
              />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Email</label>
              <input
                type="email"
                className="w-full border rounded px-3 py-2"
                value={newUser.email}
                onChange={e => setNewUser({ ...newUser, email: e.target.value })}
                required
                disabled={isCreateUserLoading}
              />
            </div>
            <p className="text-sm text-gray-500" data-cy="add-user-invitation-hint">{t('settingsPage.invitationHint')}</p>
            <div className="mb-4 mt-4">
              <label className="block text-sm font-medium mb-1">{t('settingsPage.role')}</label>
              <select
                className="w-full border rounded px-3 py-2"
                value={newUser.role}
                onChange={e => setNewUser({ ...newUser, role: e.target.value })}
                disabled={isCreateUserLoading}
              >
                <option value="user">{t('settingsPage.clientRole')}</option>
                <option value="compta">{t('settingsPage.accountantRole')}</option>
                <option value="rh">{t('settingsPage.hrRole')}</option>
                {isSuperadmin && <option value="admin">{t('settingsPage.administrator')}</option>}
              </select>
            </div>
          </form>
        )}
        {createdUserId && (
          <div className="text-center py-6">
            <p className="text-[--color-primary] font-bold" data-cy="add-user-invitation-sent">{t('settingsPage.invitationSent', { email: newUser.email })}</p>
          </div>
        )}
      </Modal>
    </>
  );
};

export default Settings;
